import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField
} from "@mui/material";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import {
  DateField,
  EditButton,
  List,
  useAutocomplete,
  useDataGrid
} from "@refinedev/mui";
import { RecycleButton } from "components/buttons/recycle";
import { RemoveButton } from "components/buttons/remove";
import React, { useState } from "react";
import UserStatus from "@warriortrading/warriortrading.hermes.common.feathers/enums/userStatus";
const filterOptions = createFilterOptions({
  matchFrom: "start",
  stringify: (option: any) => option.name,
});

export const BadgesList = () => {
  const { autocompleteProps } = useAutocomplete({
    resource: "badge-type",
    filters: [
      {
        field: "status",
        operator: "eq",
        value: 0,
      },
    ],
  });
  const { dataGridProps, setFilters, setCurrent, setPageSize } = useDataGrid({
    filters: {
      initial: [
        {
          field: "status",
          operator: "eq",
          value: 0,
        },
      ],
    },
    sorters: {
      initial: [
        {
          field: "updatedAt",
          order: "desc"
        }
      ]
    },
    pagination: {
      pageSize: 10,
    },
  });

  const columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        field: "id",
        headerName: "Id",
        type: "string",
        minWidth: 50,
        hideable: false,
      },
      {
        field: "code",
        headerName: "Code",
        minWidth: 50,
        hideable: false,
      },
      {
        field: "name",
        headerName: "Name",
        minWidth: 150,
        hideable: false,
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 100,
        hideable: false,
      },
      {
        field: "badgeUrl",
        headerName: "Badge Url",
        minWidth: 100,
        hideable: false,
        renderCell: function render({ value }) {
          return (
            <>
              <img alt="imgUrl" src={value} width={45} />
            </>
          );
        },
      },
      {
        field: "expirationDate",
        headerName: "Expiration Date",
        minWidth: 100,
        hideable: false,
        renderCell: function render({ value }) {
          return <DateField value={value} />;
        },
      },
      {
        field: "rank",
        headerName: "Rank",
        type: "string",
        minWidth: 50,
        hideable: false,
      },
      {
        field: "badgeType.name",
        headerName: "Badge Type",
        minWidth: 100,
        hideable: false,
      },
      {
        field: "status",
        headerName: "Status",
        type: "string",
        minWidth: 110,
        hideable: false,
        renderCell: function render({ row }) {
            return UserStatus.fromValue(row.status)?.name;
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        minWidth: 150,
        hideable: false,
        renderCell: function render({ row }) {
          if (row.createdAt) {
            return new Date(row.createdAt).toLocaleString();
          } else {
            return "";
          }
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        minWidth: 150,
        hideable: false,
        renderCell: function render({ row }) {
          if (row.updatedAt) {
            return new Date(row.updatedAt).toLocaleString();
          } else {
            return "";
          }
        },
      },

      {
        field: "actions",
        headerName: "Actions",
        hideable: false,
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              {row.status ? (
                <RecycleButton hideText recordItemId={row.id} />
              ) : (
                <RemoveButton hideText recordItemId={row.id} />
              )}
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 80,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [rankGte, setRankGte] = useState("");
  const [rankLte, setRankLte] = useState("");
  const [badgeType, setBadgeType] = useState("");
  const [badgeTypeValue, setBadgeTypeValue] = useState(null);
  const [statusTypeSearch, setStatusTypeSearch] = useState(0);
  const handleFilter = () => {
    setCurrent(1);
    setPageSize(10);
    let searchArr: { field: string; value: string; operator: string }[] = [];
    if (code) {
      searchArr.push({
        field: "code[$ilike]",
        value: `%${code}%`,
        operator: "eq",
      });
    }
    if (name) {
      searchArr.push({
        field: "name[$ilike]",
        value: `%${name}%`,
        operator: "eq",
      });
    }
    if (rankGte) {
      searchArr.push({
        field: "rank[$gte]",
        value: `${rankGte}`,
        operator: "eq",
      });
    }
    if (rankLte) {
      searchArr.push({
        field: "rank[$lte]",
        value: `${rankLte}`,
        operator: "eq",
      });
    }
    if (badgeType) {
      searchArr.push({
        field: "badgeTypeId",
        value: `${badgeType}`,
        operator: "eq",
      });
    }
    if (statusTypeSearch || statusTypeSearch === 0) {
      searchArr.push({
        field: "status",
        value: `${statusTypeSearch}`,
        operator: "eq",
      });
    }
    setFilters(searchArr as any);
  };

  const clearFun = () => {
    setCurrent(1);
    setPageSize(10);
    setCode("");
    setName("");
    setStatusTypeSearch(0);
    setRankGte("");
    setRankLte("");
    setBadgeType("");
    setBadgeTypeValue(null);
    setFilters([
      {
        field: "status",
        value: 0,
        operator: "eq",
      },
    ]);
  };

  return (
    <List>
      <FormControlLabel
        label=""
        style={{ width: "100%" }}
        control={
          <div style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                width: "100%",
                marginLeft: "19px",
                paddingRight: "15px",
                marginBottom: "12px",
              }}
            >
              <TextField
                id="standard-basic"
                label="Code"
                variant="standard"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{ width: "23%" }}
              />
              <TextField
                id="standard-basic"
                label="Name"
                variant="standard"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: "23%" }}
              />
              <TextField
                id="standard-basic"
                label="Rank>="
                variant="standard"
                value={rankGte}
                onChange={(e) => setRankGte(e.target.value)}
                style={{ width: "23%" }}
              />
              <TextField
                id="standard-basic"
                label="Rank<="
                variant="standard"
                value={rankLte}
                onChange={(e) => setRankLte(e.target.value)}
                style={{ width: "23%" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                width: "100%",
                marginLeft: "19px",
                paddingRight: "15px",
                marginBottom: "12px",
              }}
            >
              <Autocomplete
                {...autocompleteProps}
                value={badgeTypeValue}
                getOptionLabel={(item) => item.name}
                onChange={(event: any, value) => {
                  setBadgeType(value?.id);
                  setBadgeTypeValue(value);
                }}
                style={{ width: "30%" }}
                placeholder="Select a badge type"
                filterOptions={filterOptions}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      label="Badge Type"
                      margin="normal"
                      variant="standard"
                    />
                  );
                }}
              />
              <FormControl style={{ width: "30%", marginBottom: "8px" }}>
                <InputLabel id="status">Status</InputLabel>
                <Select
                  labelId="status"
                  value={statusTypeSearch as any}
                  label="Status"
                  variant="standard"
                  onChange={(event: SelectChangeEvent) => {
                    setStatusTypeSearch(event.target.value as any);
                  }}
                >
                  {Object.values(UserStatus)[0].map((item: any) => {
                    return (
                      <MenuItem value={item.value} key={item.value}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <div style={{ width: "30%", paddingBottom: "7px" }}>
                <ClearOutlinedIcon onClick={clearFun} />
                <SearchIcon onClick={handleFilter} />
              </div>
            </div>
          </div>
        }
      />
      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        paginationMode="server"
        rowsPerPageOptions={[10, 20, 50]}
        disableColumnFilter
      />
    </List>
  );
};
